import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, getCurrentSession, signInUser, signOutUser } from '../services/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verificar sessão ativa
    const checkSession = async () => {
      try {
        const { data: session, success } = await getCurrentSession();
        
        if (success && session) {
          const { data: currentUser, success: userSuccess } = await getCurrentUser();
          if (userSuccess) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Error checking auth session:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Verificar mudanças no localStorage para detectar login/logout em outras abas
    const handleStorageChange = () => {
      checkSession();
    };

    window.addEventListener('storage', handleStorageChange);

    // Limpar event listener ao desmontar
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Login with email and password
  const login = async (email, password) => {
    setLoading(true);
    try {
      const result = await signInUser(email, password);
      
      if (!result.success) {
        // Retornar o erro diretamente sem lançar uma exceção
        console.error('Login error:', result.error);
        return { success: false, error: result.error };
      }
      
      // Usar os dados do usuário retornados diretamente do login
      setUser(result.data.user);
      
      return { success: true, data: result.data.user };
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    setLoading(true);
    try {
      const result = await signOutUser();
      
      if (!result.success) {
        throw new Error(result.error || 'Falha ao fazer logout');
      }
      
      setUser(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
